import type { FC } from "react";

import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import { useSelector } from "@jti/store/reduxHooks";
import { Status } from "@jti/store/slices/loyaltyCoreSlice";
import { type RootStoreState } from "@jti/store/types";
import { DEPRECATED_Loading } from "@jti/ui/components/DEPRECATED_Loading";
import { Typography } from "@jti/ui/components/Typography";
import { replaceStringPlaceholder } from "@jti/ui/utils/replaceStringPlaceholder";

import useLoggedUser from "../../hooks/useLoggedUser";
import { trackCtaClick } from "../../utils/trackCtaClick";
import type { AccountActionButtonsType } from "../AccountActionButtons/AccountActionButtons";
import { AccountActionButtons } from "../AccountActionButtons/AccountActionButtons";
import { ImageBackgroundContainer } from "../ImageBackgroundContainer/ImageBackgroundContainer";

import styles from "./CallToAction.module.scss";

export type CallToActionProps = {
  variant: "cta" | "whatisploom" | "rewards";
  title: string;
  titleLoggedIn?: string;
  text?: string;
  background: string;
  description?: string;
  mobileBackground?: string;
  headerPosition?: "left" | "right";
  theme?: "light" | "dark";
  buttonImage?: string;
  buttonPosition?: "left" | "right";
  ctaTitle: string;
  ctaUrl: string;
  secondaryCtaTitle?: string;
  secondaryCtaUrl?: string;
  ctaTitleLoggedIn: string;
  ctaUrlLoggedIn: string;
  secondaryCtaTitleLoggedIn?: string;
  secondaryCtaUrlLoggedIn?: string;
  animatedBackground?: boolean;
  withGradient?: boolean;
  animatedButton?: boolean;
  animatedLabel?: string;
  restrictedContent?: boolean;
  restrictedTitle: string;
  restrictedDescription: string;
  ctaRestrictedTitle: string;
  ctaRestrictedUrl: string;
  secondaryCtaRestrictedTitle?: string;
  secondaryCtaRestrictedUrl?: string;
  ctaHeightAuto?: boolean;
};

const getTitleToShow = ({
  isLogged,
  isRestrictedContent,
  title,
  restrictedTitle,
  titleLoggedIn,
}: {
  isLogged: boolean;
  isRestrictedContent: boolean;
  title: string;
  restrictedTitle: string;
  titleLoggedIn?: string;
}): string => {
  if (isLogged && isRestrictedContent) return restrictedTitle;
  return isLogged && titleLoggedIn ? titleLoggedIn : title;
};

const getConfigVariant = (isLogged: boolean, isRestrictedContent: boolean, isRewards: boolean) => {
  if (isLogged && isRewards) return isRestrictedContent ? "restricted" : "rewards";
  return "default";
};

export const CallToAction: FC<CallToActionProps> = ({
  variant = "cta",
  buttonImage,
  theme = "light",
  headerPosition = "left",
  buttonPosition = "left",
  ctaTitle,
  ctaUrl,
  secondaryCtaTitle,
  secondaryCtaUrl,
  ctaTitleLoggedIn,
  ctaUrlLoggedIn,
  secondaryCtaTitleLoggedIn,
  secondaryCtaUrlLoggedIn,
  title,
  text,
  description,
  background,
  mobileBackground,
  titleLoggedIn,
  animatedBackground,
  withGradient,
  animatedButton,
  animatedLabel,
  restrictedContent,
  restrictedTitle,
  restrictedDescription,
  ctaRestrictedTitle,
  ctaRestrictedUrl,
  secondaryCtaRestrictedTitle,
  secondaryCtaRestrictedUrl,
  ctaHeightAuto = false,
}) => {
  const [isLogged] = useLoggedUser();
  const {
    points: { balance, status },
  } = useSelector((state: RootStoreState) => state.loyaltyCore);
  const isRestricted = useSelector((state: RootStoreState) => state.loyaltyCore.isRestricted);
  const showRestrictedContent = restrictedContent && isRestricted;
  const isRewardsAction = variant === "rewards";
  const isCallToAction = variant === "cta";
  const isWhatIsPloom = variant === "whatisploom";

  const titleToShow = getTitleToShow({
    title,
    restrictedTitle,
    isLogged,
    isRestrictedContent: !!showRestrictedContent,
    titleLoggedIn,
  });
  const shouldLazyLoadTitle =
    titleToShow.includes("userPoints") && status !== Status.COMPLETED && status !== Status.FAILED;
  const personalizedTitle = replaceStringPlaceholder(
    titleToShow,
    [{ placeholder: "userPoints", value: balance }],
    false
  );
  const textToShow = showRestrictedContent && isLogged ? restrictedDescription : text;
  const themeColor = theme === "dark" ? "black" : "white";
  const classes = classNames(styles.cta, {
    [styles.ctaDefault]: variant === "cta",
    [styles.ctaWhatIsPloom]: variant === "whatisploom",
    [styles.ctaRewards]: variant === "rewards",
    [styles.ctaLight]: theme === "light",
    [styles.ctaDark]: theme === "dark",
    [styles.ctaHeightAuto]: ctaHeightAuto && variant === "cta",
  });

  const ctaSectionClass = classNames(styles.ctaSection, {
    [styles.ctaRight]: headerPosition === "right",
  });

  const ctaSecondarySectionClass = classNames(styles.ctaSecondarySection, {
    [styles.ctaRight]: buttonPosition === "right" && !description,
  });

  const buttonsConfig: Record<string, AccountActionButtonsType> = {
    restricted: {
      signInUrl: ctaRestrictedUrl,
      signInTitle: ctaRestrictedTitle,
      registerUrl: secondaryCtaRestrictedUrl,
      registerTitle: secondaryCtaRestrictedTitle,
      trackingEvent: ctaRestrictedTitle,
    },
    rewards: {
      signInUrl: ctaUrlLoggedIn,
      signInTitle: ctaTitleLoggedIn,
      registerUrl: secondaryCtaUrlLoggedIn,
      registerTitle: secondaryCtaTitleLoggedIn,
      trackingEvent: ctaTitleLoggedIn,
    },
    default: {
      signInUrl: ctaUrl,
      signInTitle: ctaTitle,
      registerUrl: secondaryCtaUrl,
      registerTitle: secondaryCtaTitle,
    },
  };

  const configVariant = getConfigVariant(isLogged, !!showRestrictedContent, isRewardsAction);
  const showGradient = isRewardsAction || (isWhatIsPloom && withGradient);

  if (isLogged && isCallToAction) return null;

  return (
    <ImageBackgroundContainer
      background={background}
      mobileBackground={mobileBackground}
      withAnimation={animatedBackground}
    >
      <div
        className={classNames({
          [styles.ctaAnimatedButton]: animatedButton,
        })}
      >
        {showGradient && <div className={styles.ctaGradient} data-testid="call-to-action-gradient" />}
        <section className={classes} data-testid="cta">
          <div className={styles.ctaContainer}>
            <section data-testid="call-to-action-section-cta" className={ctaSectionClass}>
              {shouldLazyLoadTitle ? (
                <DEPRECATED_Loading whiteDots />
              ) : (
                <Typography
                  variant="h2"
                  weight={"light"}
                  dataTestId="cta-join-title"
                  className={classNames({
                    [styles.colorWhite]: themeColor === "white",
                    [styles.colorBlack]: themeColor === "black",
                  })}
                >
                  {personalizedTitle}
                </Typography>
              )}
              <Typography
                variant="body2"
                weight="light"
                dataTestId="cta-join-description"
                className={classNames({
                  [styles.colorWhite]: themeColor === "white",
                  [styles.colorBlack]: themeColor === "black",
                })}
              >
                {textToShow}
              </Typography>
            </section>
            {isWhatIsPloom && (
              <section data-testid="call-to-action-section-whatisploom" className={ctaSecondarySectionClass}>
                {description && (
                  <Typography
                    variant="body2"
                    weight="light"
                    dataTestId="whatisploom-description"
                    className={classNames(styles.ctaSecondarySectionDescription, {
                      [styles.colorWhite]: themeColor === "white",
                      [styles.colorBlack]: themeColor === "black",
                    })}
                  >
                    {description}
                  </Typography>
                )}
                <div className={classNames(styles.ctaAnimationWrapper, "dark-theme")}>
                  {animatedButton && animatedLabel && (
                    <Typography
                      variant="body2"
                      weight="bold"
                      className={classNames(styles.ctaAnimatedLabel, styles.colorWhite)}
                    >
                      {animatedLabel}
                    </Typography>
                  )}
                  {buttonImage ? (
                    <a href={ctaUrl} data-testid={"cta-image-button-link"} className={styles.ctaImageButton}>
                      <img src={buttonImage} alt="link-image" />
                      <Typography
                        variant="body1"
                        className={classNames(styles.ctaImageButtonText, {
                          [styles.colorWhite]: themeColor === "white",
                          [styles.colorBlack]: themeColor === "black",
                        })}
                      >
                        {ctaTitle}
                      </Typography>
                    </a>
                  ) : (
                    <Button
                      href={ctaUrl}
                      variant="primary"
                      className={styles.ctaButton}
                      data-testid="cta-register-button"
                      onClick={() => trackCtaClick(ctaTitle, "LoyaltyCallToAction")}
                    >
                      {ctaTitle}
                    </Button>
                  )}
                </div>
              </section>
            )}
          </div>
          {!isWhatIsPloom && (
            <AccountActionButtons theme="dark" {...buttonsConfig[configVariant]} className={styles.ctaAccountButtons} />
          )}
        </section>
      </div>
    </ImageBackgroundContainer>
  );
};
